import { ApiError } from '@fc/core'
import { Badge, Button, Card, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LogOut, Plus, ShieldX, UserCheck, UserCog, UserX, Users } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import {
  deactivateMember,
  listMembers,
  logoutEverywhere,
  reactivateMember,
  revokeRole,
  type Member,
} from '../../lib/members-api'
import { AssignRoleDialog } from './assign-role-dialog'
import { describeScope, MEMBER_STATUS_LABEL, MEMBER_STATUS_VARIANT } from './shared'

export function MembersList() {
  const queryClient = useQueryClient()
  const [assignTarget, setAssignTarget] = useState<Member | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['members'],
    queryFn: listMembers,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['members'] })
  const onError = (e: unknown) =>
    setActionError(e instanceof ApiError ? e.message : 'Action failed')

  const deactivate = useMutation({
    mutationFn: (id: string) => deactivateMember(id),
    onSuccess: invalidate,
    onError,
  })
  const reactivate = useMutation({
    mutationFn: (id: string) => reactivateMember(id),
    onSuccess: invalidate,
    onError,
  })
  const logout = useMutation({
    mutationFn: (id: string) => logoutEverywhere(id),
    onError,
  })
  const removeRole = useMutation({
    mutationFn: ({ userId, assignmentId }: { userId: string; assignmentId: string }) =>
      revokeRole(userId, assignmentId),
    onSuccess: invalidate,
    onError,
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Members"
        description="Manage who can access this workspace, their roles, and account status."
        actions={
          <Button asChild>
            <Link to="/app/members/invitations">
              <Plus />
              Invite people
            </Link>
          </Button>
        }
      />

      {actionError && (
        <div
          role="alert"
          className="rounded-lg border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger"
        >
          {actionError}
        </div>
      )}

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading members…
          </div>
        ) : isError ? (
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn’t load members.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'Is the API running on :3000?'}
            </p>
          </div>
        ) : !data || data.length === 0 ? (
          <div className="space-y-3 py-20 text-center">
            <Users className="mx-auto size-8 text-dim" aria-hidden="true" />
            <p className="text-sm text-muted">No members yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">Member</th>
                  <th className="px-5 py-3 font-semibold">Roles</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Last login</th>
                  <th className="px-5 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((m) => (
                  <tr key={m.id} className="border-b border-border-soft align-top last:border-0">
                    <td className="px-5 py-3">
                      <div className="font-medium">{m.name}</div>
                      <div className="text-xs text-dim">{m.email}</div>
                    </td>
                    <td className="px-5 py-3">
                      {m.assignments.length === 0 ? (
                        <span className="text-xs text-dim">No roles</span>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {m.assignments.map((a) => (
                            <span
                              key={a.id}
                              className="group inline-flex items-center gap-1 rounded-full border border-border bg-surface-2 px-2 py-0.5 text-xs text-muted"
                              title={describeScope(a.scopeType, a.scopeLabel)}
                            >
                              <span className="font-medium text-fg">{a.roleName}</span>
                              <span className="text-dim">
                                · {describeScope(a.scopeType, a.scopeLabel)}
                              </span>
                              <button
                                type="button"
                                aria-label={`Remove ${a.roleName}`}
                                onClick={() =>
                                  removeRole.mutate({ userId: m.id, assignmentId: a.id })
                                }
                                className="ml-0.5 text-dim hover:text-danger"
                              >
                                <ShieldX className="size-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <Badge variant={MEMBER_STATUS_VARIANT[m.status]}>
                        {MEMBER_STATUS_LABEL[m.status]}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-muted">
                      {m.lastLoginAt ? new Date(m.lastLoginAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setActionError(null)
                            setAssignTarget(m)
                          }}
                          title="Assign role"
                        >
                          <UserCog className="size-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => logout.mutate(m.id)}
                          title="Log out everywhere"
                        >
                          <LogOut className="size-4" />
                        </Button>
                        {m.status === 'active' ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deactivate.mutate(m.id)}
                            title="Deactivate"
                          >
                            <UserX className="size-4 text-danger" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => reactivate.mutate(m.id)}
                            title="Reactivate"
                          >
                            <UserCheck className="size-4 text-success" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {assignTarget && (
        <AssignRoleDialog member={assignTarget} onClose={() => setAssignTarget(null)} />
      )}
    </div>
  )
}
